import { UserRole } from './analytics.types';

/**
 * ONE rule for who a user is, everywhere.
 *
 * WHY THIS FILE EXISTS
 * ────────────────────────────────────────────────────────────────────────
 * Six call sites each derived the analytics id their own way:
 *
 *     customer-signin   ui.id || ui.sub
 *     customer-signup   userInfo.id
 *     customer-google   user.id || user.external_id
 *     vendor-signin     userInfo.sub
 *     vendor-register   userInfo.sub
 *     internal-login    info.id || info.email || 'internal'
 *
 * Different fallback chains for the same person means the same human can be
 * two Mixpanel users depending on which door they came through — and every
 * funnel that spans those events silently undercounts.
 *
 * THE COLLISION THIS FIXES (a real one, not hypothetical)
 * ────────────────────────────────────────────────────────────────────────
 * The three user types are issued by three DIFFERENT systems:
 *
 *     customer  → iBudgetPhoto `customers_id`   (auto-increment integer)
 *     vendor    → contributor-backend `id`      (auto-increment integer)
 *     internal  → Cognito user id
 *
 * The first two are plain integers from unrelated databases, so customer 5018
 * and vendor 5018 were landing on the SAME Mixpanel profile: merged events,
 * a People profile blending a buyer and a supplier, and any cross-role funnel
 * quietly wrong.
 *
 * Namespacing by role makes a collision structurally impossible.
 *
 * THE BACKEND MUST APPLY THE SAME RULE
 * ────────────────────────────────────────────────────────────────────────
 * Laravel derives its distinct_id from the validated JWT — never from
 * anything the client sends, or a caller could attribute events to someone
 * else. It must produce the identical string, or server-side events (proposal
 * received, TOE signed, payments) land on a second profile and the funnel
 * breaks exactly where it matters most.
 *
 *     PHP: $role . '_' . $id
 */

/** Prefix per role. Changing these orphans existing Mixpanel profiles. */
const ROLE_PREFIX: Record<Exclude<UserRole, 'anonymous'>, string> = {
  customer: 'customer',
  vendor: 'vendor',
  internal: 'internal',
};

/**
 * Build the namespaced distinct_id.
 *
 * Returns null when there is no usable id — deliberately, so callers skip
 * `identify()` rather than sending a junk id. Mixpanel treats a bad
 * distinct_id as a real user, and those are impossible to clean up later.
 */
export function analyticsDistinctId(
  role: Exclude<UserRole, 'anonymous'>,
  rawId: unknown,
): string | null {
  const id = String(rawId ?? '').trim();
  if (!id || id === 'undefined' || id === 'null') return null;
  return `${ROLE_PREFIX[role]}_${id}`;
}

/**
 * Pull the raw id out of a stored `userInfo` blob.
 *
 * The three login flows write different field names for the same concept, and
 * that is not worth changing now — those blobs are read by unrelated code
 * (guards, headers, the vendor console) and renaming a field there risks far
 * more than it fixes. So the variation is absorbed HERE, in one place, where
 * it is visible and documented.
 *
 *     customer  `id`  ← external_id (iBudgetPhoto customers_id)
 *     vendor    `sub` ← contributor-backend id
 *     internal  `id`  ← Cognito id
 */
export function rawIdFromUserInfo(role: Exclude<UserRole, 'anonymous'>, info: any): unknown {
  if (!info) return null;
  switch (role) {
    case 'customer':
      // `external_id` last: it is the same value, but only some flows write it.
      return info.id ?? info.external_id ?? null;
    case 'vendor':
      // CONTRIBUTOR CODE, not the numeric id.
      //
      // This previously used `sub` (the numeric contributor id), which split
      // every vendor across two Mixpanel profiles: the browser wrote
      // `vendor_6868` while the backend — whose payloads carry the CODE, not
      // the id — wrote `vendor_IQII`. The vendor funnel
      // (invited -> viewed -> bid -> assigned) had its first and last steps on
      // one profile and its middle on another, so it could not be built at all.
      //
      // The code wins because it is the identifier the rest of the system
      // already speaks: invitations, proposals, bids and Solr all key on
      // contributor_code, and it is what arrives in every backend payload.
      // The numeric id appears only in the login response.
      //
      // The backend MUST agree — see MixpanelService::distinctId().
      return info['custom:team'] ?? info.contributor_code ?? info.sub ?? info.id ?? null;
    case 'internal':
      // Email is a legitimate last resort here — internal users are few and
      // their Cognito id is not always present on the login payload. It is
      // still stable and unique, which is all a distinct_id has to be.
      return info.id ?? info.email ?? null;
    default:
      return null;
  }
}

/** Convenience: read `userInfo` from storage and resolve in one step. */
export function resolveIdentity(
  role: Exclude<UserRole, 'anonymous'>,
  info?: any,
): string | null {
  let source = info;
  if (!source) {
    try {
      const raw = localStorage.getItem('userInfo');
      source = raw ? JSON.parse(raw) : null;
    } catch {
      source = null;
    }
  }
  return analyticsDistinctId(role, rawIdFromUserInfo(role, source));
}
